"use client";

import { usePathname, useRouter } from "next/navigation";
import { usePortfolioContext } from "@/contexts/portfolio-context";
import { useAuthUser } from "@/hooks/use-auth-user";
import { cn } from "@/lib/utils";
import { FontSizeControl } from "./font-size-control";

export function PortfolioTabs() {
  const { portfolios, selectedPortfolio, setSelectedPortfolio } =
    usePortfolioContext();
  const pathname = usePathname();
  const router = useRouter();
  const { restricted } = useAuthUser();
  // 선택된 포트폴리오가 momentum 전략이면 탭 바 배경도 붉은 계열로
  const isMomentum = portfolios.find((p) => p.name === selectedPortfolio)?.strategy === "momentum";
  // 좌: 역추세(및 기타), 우: momentum
  const momentumPfs = portfolios.filter((p) => p.strategy === "momentum");
  const otherPfs = portfolios.filter((p) => p.strategy !== "momentum");

  // 포트폴리오 선택 — 대시보드에 있을 때 누르면 해당 포트폴리오 보유종목으로 이동.
  // (그 외 라우트에선 선택만 바꿔 현재 화면 유지)
  const selectPortfolio = (name: string) => {
    setSelectedPortfolio(name);
    if (pathname === "/dashboard") router.push("/holding");
  };

  return (
    <div className="w-full border-b border-black/[0.06]" style={{
      background: isMomentum ? "rgba(251, 242, 242, 0.92)" : "rgba(229, 229, 234, 0.92)",
      backdropFilter: "saturate(180%) blur(20px)",
      WebkitBackdropFilter: "saturate(180%) blur(20px)",
    }}>
      <div className="w-full flex h-[48px] items-center px-3 md:px-6 min-w-0">
        <span className="text-neutral-900 font-semibold text-base tracking-[-0.374px] mr-4 md:mr-8 shrink-0">
          EzSplit
        </span>
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar scroll-x-only -mx-1 px-1 min-w-0 flex-1">
          {/* 대시보드 — 맨 왼쪽 고정 (헤더 메뉴에서 이동) · 회색 — 포트폴리오 탭과 구분 */}
          {!restricted && (
            <button
              onClick={() => router.push("/dashboard")}
              className={cn(
                "flex items-center px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap shrink-0",
                pathname === "/dashboard"
                  ? "bg-[#6e6e73] text-white font-medium"                    // 선택: 진회색
                  : "bg-[#d1d1d6] text-black/70 font-medium hover:bg-[#c7c7cc] border border-black/10"  // 미선택: 연회색
              )}
            >
              대시보드
            </button>
          )}
          {/* 역추세(및 기타) 포트폴리오 — 왼쪽 */}
          {otherPfs.map((p) => (
            <TabButton
              key={p.name}
              label={p.name}
              badge={p.market === "overseas" ? "US" : "KR"}
              isActive={selectedPortfolio === p.name}
              isMomentum={false}
              onClick={() => selectPortfolio(p.name)}
            />
          ))}
          {/* momentum 전략 포트폴리오 — 역추세 바로 오른쪽 (여백 없이) */}
          {momentumPfs.map((p) => (
            <TabButton
              key={p.name}
              label={p.name}
              badge={p.market === "overseas" ? "US" : "KR"}
              isActive={selectedPortfolio === p.name}
              isMomentum={true}
              onClick={() => selectPortfolio(p.name)}
            />
          ))}
        </div>
        {/* 화면 폰트 크기 — 포트폴리오 목록 반대편 오른쪽 (데스크톱만) */}
        <FontSizeControl className="hidden md:flex shrink-0 ml-3 md:ml-4" />
      </div>
    </div>
  );
}

function TabButton({
  label,
  badge,
  isActive,
  isMomentum = false,
  onClick,
}: {
  label: string;
  badge?: string;
  isActive: boolean;
  isMomentum?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1 text-sm rounded-full transition-colors whitespace-nowrap shrink-0",
        isActive
          ? isMomentum
            ? "bg-[#ff3b30] text-white font-medium"      // momentum 선택: 빨강 배경
            : "bg-[#0071e3] text-white font-medium"
          : isMomentum
            ? "bg-white text-[#ff3b30] font-bold border border-[#ff3b30]"  // momentum 미선택: 흰 바탕·빨간 굵은 글씨·빨간 테두리
            : "bg-white text-black/85 font-medium hover:text-neutral-900 border border-[#0071e3]"  // 역추세 미선택: 파란 테두리
      )}
    >
      <span>{label}</span>
      {badge && (
        <span className={cn(
          "text-3xs font-semibold px-1 py-0.5 rounded",
          isActive ? "bg-white/30 text-white" : "bg-black/[0.08] text-black/70"
        )}>
          {badge}
        </span>
      )}
    </button>
  );
}
